// src/pages/Admin.jsx
import React, { useState, useEffect } from "react";
import {
  collection,
  doc,
  addDoc,
  getDocs,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../components/firebase/firebase";
import { useNavigate } from "react-router-dom";
import Loader from "../components/common/Loader";
import Alert from "../components/common/Alert";

const Admin = () => {
  const [cards, setCards] = useState({});
  const [degree, setDegree] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [exam, setExam] = useState("");
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [referenceLink, setReferenceLink] = useState("");
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("");
  const [adTitle, setAdTitle] = useState("");
  const [adImage, setAdImage] = useState(null);
  const [ads, setAds] = useState([]);

  // FORMS MANAGEMENT
  const [formTitle, setFormTitle] = useState("");
  const [formLink, setFormLink] = useState("");
  const [forms, setForms] = useState([]);

  useEffect(() => {
    const getPricingData = async () => {
      setLoading(true);
      try {
        const pricingRef = collection(db, "pricing");
        const pricingSnap = await getDocs(pricingRef);
        let data = pricingSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const order = [
          "10th12th-mon",
          "10th12th-half",
          "10th12th-ann",
          "jee-mon",
          "jee-half",
          "jee-ann",
          "neet-mon",
          "neet-half",
          "neet-ann",
        ];
        data = data.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));

        const groupedData = data.reduce((acc, curr) => {
          const category = curr.id.split("-")[0];
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(curr);
          return acc;
        }, {});

        setCards(groupedData);
      } catch (err) {
        console.error("Error fetching pricing:", err);
      } finally {
        setLoading(false);
      }
    };

    getPricingData();
  }, []);

  useEffect(() => {
    if (
      degree &&
      year &&
      month &&
      title &&
      ((degree !== "12thCbse" &&
        degree !== "12thState" &&
        degree !== "11thCbse" &&
        degree !== "11thState" &&
        degree !== "10thCbse" &&
        degree !== "10thState" &&
        degree !== "9thCbse" &&
        degree !== "9thState" &&
        degree !== "8thCbse" &&
        degree !== "8thState" &&
        degree !== "7thCbse" &&
        degree !== "7thState" &&
        degree !== "6thCbse" &&
        degree !== "6thState") ||
        subject)
    ) {
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [degree, year, month, title, subject]);

  const handleInputChange = (category, index, event) => {
    const newCards = { ...cards };
    newCards[category][index][Object.keys(newCards[category][index])[1]] =
      event.target.value;
    setCards(newCards);
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      for (const category of Object.keys(cards)) {
        for (const card of cards[category]) {
          const docRef = doc(db, "pricing", card.id);
          await setDoc(docRef, card, { merge: true });
        }
      }
      setAlertMessage("Pricing updated successfully");
      setAlertType("success");
      setShowAlert(true);
    } catch (error) {
      setAlertMessage("Error updating pricing");
      setAlertType("error");
      setShowAlert(true);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    setLoading(true);
    try {
      const uploadedFile = e.target.files[0];
      if (!uploadedFile) return;

      const timestamp = Date.now();
      const fileName = `${uploadedFile.name.split(".")[0]}_${timestamp}.${
        uploadedFile.name.split(".").pop()
      }`;

      setFile(uploadedFile);

      const storageRef = ref(storage, `${degree}/${fileName}`);
      await uploadBytes(storageRef, uploadedFile);
      const fileURL = await getDownloadURL(storageRef);

      let thumbnailURL = "";
      if (thumbnail) {
        const thumbnailName = `${thumbnail.name.split(".")[0]}_${timestamp}.${
          thumbnail.name.split(".").pop()
        }`;
        const thumbnailRef = ref(storage, `thumbnail/${thumbnailName}`);
        await uploadBytes(thumbnailRef, thumbnail);
        thumbnailURL = await getDownloadURL(thumbnailRef);
      }

      const resourceData = {
        link: fileURL,
        thumbnail: thumbnailURL,
        referenceLink: referenceLink,
        title: title,
        type: getTypeFromDegree(degree),
        grade: getGradeFromDegree(degree),
        exam: exam,
        subject: subject || "all",
        month: month,
        year: year,
      };

      await addDoc(collection(db, "resources"), resourceData);
      setAlertMessage("File uploaded successfully");
      setAlertType("success");
      setShowAlert(true);
    } catch (error) {
      setAlertMessage("Error uploading file");
      setAlertType("error");
      setShowAlert(true);
      console.error("upload error:", error);
    } finally {
      setDegree("");
      setYear("");
      setMonth("");
      setTitle("");
      setSubject("");
      setFile(null);
      setThumbnail(null);
      setReferenceLink("");
      setLoading(false);
      fetchPDFs();
    }
  };

  const getTypeFromDegree = (degree) => {
    switch (degree) {
      case "10thCbse":
      case "11thCbse":
      case "12thCbse":
      case "9thCbse":
      case "8thCbse":
      case "7thCbse":
      case "6thCbse":
        return "cbse";
      case "10thState":
      case "11thState":
      case "12thState":
      case "9thState":
      case "8thState":
      case "7thState":
      case "6thState":
        return "state";
      case "jee":
        return "jee";
      case "neet":
        return "neet";
      case "tnpsc":
        return "tnpsc";
      default:
        return "";
    }
  };

  const getGradeFromDegree = (degree) => {
    switch (degree) {
      case "12thCbse":
      case "12thState":
        return "12";
      case "11thCbse":
      case "11thState":
        return "11";
      case "10thCbse":
      case "10thState":
        return "10";
      case "9thCbse":
      case "9thState":
        return "9";
      case "8thCbse":
      case "8thState":
        return "8";
      case "7thCbse":
      case "7thState":
        return "7";
      case "6thCbse":
      case "6thState":
        return "6";
      case "jee":
      case "neet":
      case "tnpsc":
        return "previous-years";
      default:
        return "";
    }
  };

  const categoryNames = {
    "10th12th": "10 & 12th CBSE/State",
    jee: "JEE",
    neet: "NEET",
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const getYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 1990; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const fetchPDFs = async () => {
    setLoading(true);
    try {
      const pdfsRef = collection(db, "resources");
      const pdfsSnapshot = await getDocs(pdfsRef);
      const pdfsList = pdfsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPdfs(pdfsList);
    } catch (err) {
      console.error("Error fetching pdfs:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAds = async () => {
    setLoading(true);
    try {
      const adsRef = collection(db, "ads");
      const adsSnapshot = await getDocs(adsRef);
      const adsList = adsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAds(adsList);
    } catch (err) {
      console.error("Error fetching ads:", err);
    } finally {
      setLoading(false);
    }
  };

  // FETCH FORMS
  const fetchForms = async () => {
    setLoading(true);
    try {
      const formsRef = collection(db, "forms");
      const formsSnapshot = await getDocs(formsRef);
      const formsList = formsSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return tb - ta;
        });
      setForms(formsList);
    } catch (error) {
      console.error("Error fetching forms:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPDFs();
    fetchAds();
    fetchForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handlePDFDelete(pdfId, pdfLink, thumbnailLink) {
    setLoading(true);
    try {
      await deleteObject(ref(storage, pdfLink));

      if (thumbnailLink) {
        await deleteObject(ref(storage, thumbnailLink));
      }
      await deleteDoc(doc(db, "resources", pdfId));

      setPdfs((prevPdfs) => prevPdfs.filter((pdf) => pdf.id !== pdfId));
      setAlertMessage("PDF deleted successfully");
      setAlertType("success");
      setShowAlert(true);
    } catch (error) {
      console.error("Error deleting PDF:", error);
      setAlertMessage("Error deleting PDF");
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  }

  const handleAdUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!adImage || !adTitle) {
        setAlertMessage("Please fill all ad fields.");
        setAlertType("error");
        setShowAlert(true);
        setLoading(false);
        return;
      }

      const timestamp = Date.now();
      const adImageName = `ads/${adImage.name.split(".")[0]}_${timestamp}.${
        adImage.name.split(".").pop()
      }`;
      const storageRef = ref(storage, adImageName);
      await uploadBytes(storageRef, adImage);
      const adImageURL = await getDownloadURL(storageRef);

      const adData = {
        title: adTitle,
        imageURL: adImageURL,
      };

      await addDoc(collection(db, "ads"), adData);
      setAlertMessage("Ad uploaded successfully");
      setAlertType("success");
      setShowAlert(true);
      setAdTitle("");
      setAdImage(null);
      fetchAds();
    } catch (error) {
      console.error("Error uploading ad:", error);
      setAlertMessage("Error uploading ad");
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAdDelete = async (adId, adImageURL) => {
    setLoading(true);
    try {
      const adRef = ref(storage, adImageURL);
      await deleteObject(adRef);
      await deleteDoc(doc(db, "ads", adId));
      setAds((prevAds) => prevAds.filter((ad) => ad.id !== adId));
      setAlertMessage("Ad deleted successfully");
      setAlertType("success");
      setShowAlert(true);
    } catch (error) {
      console.error("Error deleting ad:", error);
      setAlertMessage("Error deleting ad");
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // FORM HANDLERS
  const handleFormUpload = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!formTitle || !formLink) {
        setAlertMessage("Please provide form title and link.");
        setAlertType("error");
        setShowAlert(true);
        setLoading(false);
        return;
      }

      const formData = {
        title: formTitle,
        link: formLink,
        createdAt: new Date().toISOString(),
      };

      await addDoc(collection(db, "forms"), formData);
      setAlertMessage("Form added successfully");
      setAlertType("success");
      setShowAlert(true);
      setFormTitle("");
      setFormLink("");
      fetchForms();
    } catch (error) {
      console.error("Error adding form:", error);
      setAlertMessage("Error adding form");
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFormDelete = async (formId) => {
    setLoading(true);
    try {
      await deleteDoc(doc(db, "forms", formId));
      setForms((prev) => prev.filter((f) => f.id !== formId));
      setAlertMessage("Form deleted successfully");
      setAlertType("success");
      setShowAlert(true);
    } catch (error) {
      console.error("Error deleting form:", error);
      setAlertMessage("Error deleting form");
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const subjects = [
    { title: "Mathematics", key: "mathematics" },
    { title: "Science", key: "science" },
    { title: "Social Science", key: "social-science" },
    { title: "English", key: "english" },
    { title: "தமிழ்", key: "tamil" },
    { title: "Business Maths", key: "business-maths" },
    { title: "Physics", key: "physics" },
    { title: "Chemistry", key: "chemistry" },
    { title: "Biology", key: "biology" },
    { title: "Computer Science", key: "computer-science" },
    { title: "Accountancy", key: "accountancy" },
    { title: "Commerce", key: "commerce" },
    { title: "Economics", key: "economics" },
    { title: "History", key: "history" },
    { title: "Computer Applications", key: "computer-application" },
    { title: "zoology", key: "zoology" },
    { title: "botany", key: "botany" },
  ];

  const examination = [
    { title: "First Mid Term", key: "first-mid-term" },
    { title: "Quarterly", key: "quarterly" },
    { title: "Second Mid Term", key: "second-mid-term" },
    { title: "Half Yearly", key: "half-yearly" },
    { title: "Third Mid Term", key: "third-mid-term" },
    { title: "First Revision", key: "first-revision" },
    { title: "Second Revision", key: "second-revision" },
    { title: "Third Revision", key: "third-revision" },
    { title: "Model Board", key: "model-board" },
    { title: "Study Materials", key: "study-materials" },
  ];

  const formatDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-400 p-8 flex flex-col items-center justify-center">
      {showAlert && <Alert type={alertType} message={alertMessage} />}
      <div className="absolute top-20 right-8">
        <button
          onClick={handleLogout}
          className="relative px-5 py-3 overflow-hidden font-medium text-blue-600 bg-blue-100 rounded-full shadow-inner group"
        >
          <span className="absolute top-0 left-0 w-0 h-0 transition-all duration-200 border-t-2 border-blue-600 group-hover:w-full ease"></span>
          <span className="absolute bottom-0 right-0 w-0 h-0 transition-all duration-200 border-b-2 border-blue-600 group-hover:w-full ease"></span>
          <span className="absolute top-0 left-0 w-full h-0 transition-all duration-300 delay-200 bg-blue-600 group-hover:h-full ease"></span>
          <span className="absolute bottom-0 left-0 w-full h-0 transition-all duration-300 delay-200 bg-blue-600 group-hover:h-full ease"></span>
          <span className="absolute inset-0 w-full h-full duration-300 delay-300 bg-blue-900 opacity-0 group-hover:opacity-100"></span>
          <span className="relative transition-colors duration-300 delay-200 group-hover:text-white ease">
            Logout
          </span>
        </button>
      </div>
      <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-4xl">
        {loading ? (
          <Loader />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-8">
              <div className="p-4 border-2 border-indigo-500 rounded-xl">
                <h2 className="text-2xl font-bold mb-4 text-center">
                  Pricing Section
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {Object.keys(cards).map((category, index) => (
                    <div key={index} className="flex flex-col gap-4">
                      <h3 className="text-lg text-blue-500 font-semibold bg-gray-200 px-3.5 py-1.5 rounded">
                        {categoryNames[category] || category}
                      </h3>
                      {cards[category].map((card, idx) => (
                        <input
                          key={idx}
                          type="text"
                          value={card[Object.keys(card)[1]]}
                          onChange={(e) => handleInputChange(category, idx, e)}
                          className="p-2 border border-gray-300 rounded"
                        />
                      ))}
                    </div>
                  ))}
                </div>
                <div className="flex justify-center mt-6">
                  <button
                    onClick={handleUpdate}
                    className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
                  >
                    Update
                  </button>
                </div>
              </div>

              <div className="p-4 border-2 border-indigo-500 rounded-xl">
                <h2 className="text-2xl font-bold mb-4 text-center">Upload PDF</h2>
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-3 gap-4">
                    <select
                      value={degree}
                      onChange={(e) => setDegree(e.target.value)}
                      className="p-2 border border-gray-300 rounded"
                    >
                      <option value="">Select Degree</option>
                      <option value="12thState">12th</option>
                      <option value="11thState">11th</option>
                      <option value="10thState">10th</option>
                      <option value="9thState">9th</option>
                    </select>
                    <select
                      value={year}
                      onChange={(e) => setYear(e.target.value)}
                      className="p-2 border border-gray-300 rounded"
                    >
                      <option value="">Select Year</option>
                      {getYears().map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <select
                      value={month}
                      onChange={(e) => setMonth(e.target.value)}
                      className="p-2 border border-gray-300 rounded"
                    >
                      <option value="">Select Month</option>
                      {months.map((m, index) => (
                        <option key={index} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    placeholder="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                  />
                  {(degree === "12thCbse" ||
                    degree === "11thCbse" ||
                    degree === "10thCbse" ||
                    degree === "9thCbse" ||
                    degree === "8thCbse" ||
                    degree === "7thCbse" ||
                    degree === "6thCbse" ||
                    degree === "12thState" ||
                    degree === "11thState" ||
                    degree === "10thState" ||
                    degree === "9thState" ||
                    degree === "8thState" ||
                    degree === "7thState" ||
                    degree === "6thState") && (
                    <select
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="p-2 border border-gray-300 rounded"
                    >
                      <option value="">Select Subject</option>
                      {subjects.map((sub, index) => (
                        <option key={index} value={sub.key}>
                          {sub.title}
                        </option>
                      ))}
                    </select>
                  )}
                  <select
                    value={exam}
                    onChange={(e) => setExam(e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                  >
                    <option value="">Select Exam</option>
                    {examination.map((ex, index) => (
                      <option key={index} value={ex.key}>
                        {ex.title}
                      </option>
                    ))}
                  </select>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setThumbnail(e.target.files[0])}
                    className="p-2 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    placeholder="Reference Link"
                    value={referenceLink}
                    onChange={(e) => setReferenceLink(e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                  />
                  <div className="flex flex-col items-center justify-center border-4 border-dashed border-gray-300 rounded-lg p-4 h-64">
                    <input
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="fileUpload"
                      disabled={isButtonDisabled}
                    />
                    <label
                      htmlFor="fileUpload"
                      className={`cursor-pointer py-2 px-4 rounded-lg transition-colors ${
                        isButtonDisabled
                          ? "bg-gray-300"
                          : "bg-blue-500 text-white hover:bg-blue-400"
                      }`}
                    >
                      {file ? "File Selected: " + file.name : "Choose a PDF File"}
                    </label>
                    <p className="mt-4 text-gray-500 text-center">
                      Drag and drop a PDF file here or click to select a file
                      from your computer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-4 mt-8 border-2 border-indigo-500 rounded-xl">
                <h2 className="text-2xl font-bold mb-4 text-center">Uploaded PDFs</h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {pdfs.map((pdf) => (
                    <div key={pdf.id} className="flex flex-col gap-2 border p-4 rounded">
                      <p>{pdf.title}</p>
                      <img src={pdf.thumbnail} alt="Thumbnail" className="w-full h-32 object-cover" />
                      <a href={pdf.referenceLink} className="text-blue-500" target="_blank" rel="noopener noreferrer">
                        Reference Link
                      </a>
                      <button
                        onClick={() => handlePDFDelete(pdf.id, pdf.link, pdf.thumbnail)}
                        className="px-2 py-1 mt-2 text-white bg-red-500 rounded hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Ad Management */}
              <div className="p-4 mt-8 border-2 border-green-500 rounded-xl">
                <h2 className="text-2xl font-bold mb-4 text-center">Ad Management</h2>
                <div className="flex flex-col gap-4">
                  <input
                    type="text"
                    placeholder="Ad Title"
                    value={adTitle}
                    onChange={(e) => setAdTitle(e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setAdImage(e.target.files[0])}
                    className="p-2 border border-gray-300 rounded"
                  />
                  <button
                    onClick={handleAdUpload}
                    className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600"
                    disabled={!adTitle || !adImage}
                  >
                    Upload Ad
                  </button>
                </div>
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-4">Uploaded Ads</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {ads.map((ad) => (
                      <div key={ad.id} className="border p-4 rounded flex flex-col items-center">
                        <h4 className="font-semibold">{ad.title}</h4>
                        <img src={ad.imageURL} alt={ad.title} className="w-full h-auto mt-2 rounded" />
                        {ad.redirectLink && (
                          <a href={ad.redirectLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline mt-2">
                            Visit Ad
                          </a>
                        )}
                        <button onClick={() => handleAdDelete(ad.id, ad.imageURL)} className="mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700">
                          Delete Ad
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Form Management (NEW) */}
              <div className="p-4 mt-8 border-2 border-yellow-500 rounded-xl">
                <h2 className="text-2xl font-bold mb-4 text-center">Form Management</h2>
                <form onSubmit={handleFormUpload} className="flex flex-col gap-3 max-w-2xl mx-auto">
                  <input
                    type="text"
                    placeholder="Form Title"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                  />
                  <input
                    type="url"
                    placeholder="Form Link (https://...)"
                    value={formLink}
                    onChange={(e) => setFormLink(e.target.value)}
                    className="p-2 border border-gray-300 rounded"
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
                      disabled={!formTitle || !formLink}
                    >
                      Add Form
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormTitle("");
                        setFormLink("");
                      }}
                      className="px-4 py-2 rounded border"
                    >
                      Clear
                    </button>
                  </div>
                </form>

                <div className="mt-6">
                  <h3 className="text-xl font-bold mb-3">Uploaded Forms</h3>
                  {forms.length === 0 ? (
                    <p className="text-gray-500">No forms uploaded yet.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {forms.map((f) => (
                        <div key={f.id} className="border p-3 rounded flex items-center justify-between gap-3">
                          <div>
                            <p className="font-semibold truncate">{f.title}</p>
                            <p className="text-xs text-gray-500">{formatDate(f.createdAt)}</p>
                          </div>
                          <div className="flex flex-col items-end">
                            <a href={f.link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline mb-2">
                              Open
                            </a>
                            <button onClick={() => handleFormDelete(f.id)} className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700">
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
